import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import StepBar from "./StepBar";

const FAMILLES_5M = [
  { icon: "🏭", label: "Milieu", value: "milieu" },
  { icon: "👥", label: "Main-d'oeuvre", value: "main_oeuvre" },
  { icon: "🔧", label: "Matériel", value: "materiel" },
  { icon: "📦", label: "Matières", value: "matiere" },
  { icon: "📐", label: "Méthodes", value: "methode" },
];

const fieldLabelSx = {
  color: "#536b91",
  display: "block",
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.06em",
  mb: 0.55,
  textTransform: "uppercase",
  "& .MuiFormLabel-asterisk": { color: "#d32f2f" },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "8px",
    fontSize: 13,
  },
};

const textareaSx = (error) => ({
  backgroundColor: "#fff",
  border: `1px solid ${error ? "#d32f2f" : "#c7d3e4"}`,
  borderRadius: "8px",
  boxSizing: "border-box",
  color: "#10263d",
  fontFamily: "inherit",
  fontSize: 13,
  lineHeight: 1.5,
  minHeight: 70,
  outline: "none",
  p: 1.25,
  resize: "vertical",
  transition: "border-color 150ms ease, box-shadow 150ms ease",
  width: "100%",
  "&:hover": { borderColor: "#7890aa" },
  "&:focus": {
    borderColor: error ? "#d32f2f" : "#60758a",
    boxShadow: error
      ? "0 0 0 2px rgba(211, 47, 47, 0.12)"
      : "0 0 0 2px rgba(96, 117, 138, 0.14)",
  },
  "&::placeholder": { color: "#8a9aae", opacity: 1 },
});

const normalizeFiveMValues = (value) => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const selectedFamilies = (value) => {
  const values = normalizeFiveMValues(value);
  return FAMILLES_5M.filter((option) => values.includes(option.value));
};

const AnalyseStep = ({
  formData,
  navigationEnabled,
  onBack,
  onChange,
  onWhyAdd,
  onWhyChange,
  onWhyRemove,
  onSave,
  onStepChange,
  saving,
}) => {
  const [errors, setErrors] = useState({});
  const problemRef = useRef(null);
  const firstWhyRef = useRef(null);
  const rootCauseRef = useRef(null);
  const initialProblemAppliedRef = useRef(false);

  useEffect(() => {
    if (initialProblemAppliedRef.current) return;

    const currentProblem = String(
      formData.analyse_probleme_depart || ""
    ).trim();
    if (currentProblem) {
      initialProblemAppliedRef.current = true;
      return;
    }

    const constatDescription = String(formData.description_constat || "").trim();
    if (!constatDescription) return;

    initialProblemAppliedRef.current = true;
    onChange("analyse_probleme_depart", constatDescription);
  }, [
    formData.analyse_probleme_depart,
    formData.description_constat,
    onChange,
  ]);

  const pourquois = useMemo(() => {
    const rows = Array.isArray(formData.analyse_pourquois)
      ? formData.analyse_pourquois
      : [];
    if (rows.length >= 3) return rows;
    return [
      ...rows,
      ...Array.from({ length: 3 - rows.length }, () => ({
        famille_5m: "",
        texte: "",
      })),
    ];
  }, [formData.analyse_pourquois]);

  const updateWhy = (index, patch) => {
    onWhyChange(index, patch);
    setErrors((current) => {
      const whyErrors = { ...(current.pourquois || {}) };
      whyErrors[index] = { ...(whyErrors[index] || {}), ...patch };
      Object.keys(patch).forEach((key) => {
        whyErrors[index][key] = undefined;
      });
      return { ...current, pourquois: whyErrors };
    });
  };

  const updateRootCause = (value) => {
    onChange("analyse_cause_racine", value);
    setErrors((current) => ({ ...current, analyse_cause_racine: undefined }));
  };

  const updateProblem = (value) => {
    onChange("analyse_probleme_depart", value);
    setErrors((current) => ({
      ...current,
      analyse_probleme_depart: undefined,
    }));
  };

  const validate = () => {
    const nextErrors = {};
    const whyErrors = {};
    const filledWhys = pourquois.filter((item) =>
      String(item?.texte || "").trim()
    );

    if (!String(formData.analyse_probleme_depart || "").trim()) {
      nextErrors.analyse_probleme_depart =
        "Le problème de départ est obligatoire.";
    }

    if (!filledWhys.length) {
      whyErrors[0] = {
        ...(whyErrors[0] || {}),
        texte: "Au moins un pourquoi est obligatoire.",
      };
    }

    pourquois.forEach((item, index) => {
      const texte = String(item?.texte || "").trim();
      const familles = normalizeFiveMValues(item?.famille_5m);
      if (familles.length && !texte) {
        whyErrors[index] = {
          ...(whyErrors[index] || {}),
          texte: "Le texte du Pourquoi est obligatoire.",
        };
      }
      if (texte && !familles.length) {
        whyErrors[index] = {
          ...(whyErrors[index] || {}),
          famille_5m: "Au moins une famille 5M est obligatoire.",
        };
      }
    });

    if (!String(formData.analyse_cause_racine || "").trim()) {
      nextErrors.analyse_cause_racine = "La cause racine est obligatoire.";
    }
    if (Object.keys(whyErrors).length) nextErrors.pourquois = whyErrors;

    setErrors(nextErrors);
    if (nextErrors.analyse_probleme_depart) {
      problemRef.current?.focus();
    } else if (nextErrors.pourquois) {
      firstWhyRef.current?.focus();
    } else if (nextErrors.analyse_cause_racine) {
      rootCauseRef.current?.focus();
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleLocalSave = () => {
    if (!saving && validate()) onSave(null);
  };

  const handleSave = (destination) => {
    if (!saving && validate()) onSave(destination);
  };

  return (
    <Box
      aria-label="Étape analyse des causes"
      role="region"
      sx={{
        backgroundColor: "#f1f5fb",
        minHeight: "calc(100vh - 56px)",
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <StepBar
        activeStep={3}
        navigationEnabled={navigationEnabled}
        onStepChange={onStepChange}
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #cbd8e6",
          borderRadius: "12px",
          mt: 2.5,
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            borderBottom: "1px solid #d5e0eb",
            display: "flex",
            gap: 1.5,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: "#eef3f8",
              borderRadius: "9px",
              color: "#36546f",
              display: "flex",
              height: 34,
              justifyContent: "center",
              width: 34,
            }}
          >
            <SearchOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography color="#10263d" fontSize={14} fontWeight={800}>
              Étape 4 — Analyse des causes
            </Typography>
            <Typography color="#60758a" fontSize={11}>
              Analyser la cause racine avec N Pourquoi
            </Typography>
          </Box>
        </Box>

        <Alert
          severity="info"
          sx={{
            backgroundColor: "#eef8fb",
            border: "1px solid #b7dbe5",
            color: "#315b68",
            mt: 2,
          }}
        >
          Utilisez N Pourquoi pour structurer l&apos;analyse. Pour chaque niveau,
          associez une famille 5M afin de catégoriser l&apos;origine.
        </Alert>

        <Grid container spacing={1.7} sx={{ mt: 0.2 }}>
          <Grid item xs={12}>
            <Typography sx={fieldLabelSx}>Outil d'analyse utilisé</Typography>
            <Box
              sx={{
                border: "1px solid #c7d3e4",
                borderRadius: "16px",
                color: "#36546f",
                display: "inline-flex",
                fontSize: 12,
                fontWeight: 800,
                px: 1.6,
                py: 0.7,
              }}
            >
              N Pourquoi + 5M
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: "#f8fbfd",
                border: "1px solid #b8c8d9",
                borderRadius: "10px",
                p: { xs: 1.5, sm: 2 },
              }}
            >
              <Typography
                color="#36546f"
                fontSize={12}
                fontWeight={800}
                sx={{ mb: 1.5, textTransform: "uppercase" }}
              >
                N Pourquoi — Analyse des causes
              </Typography>

              <FormLabel required sx={fieldLabelSx}>
                Problème de départ
              </FormLabel>
              <TextField
                error={Boolean(errors.analyse_probleme_depart)}
                fullWidth
                helperText={errors.analyse_probleme_depart || " "}
                inputRef={problemRef}
                onChange={(event) => updateProblem(event.target.value)}
                size="small"
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fde5e5",
                    borderRadius: "8px",
                    color: "#7c4f4f",
                    fontSize: 13,
                    fontWeight: 800,
                    "& fieldset": { borderColor: "#ff2f2f", borderWidth: 1.5 },
                    "&:hover fieldset": { borderColor: "#ff2f2f" },
                    "&.Mui-focused fieldset": { borderColor: "#ff2f2f" },
                  },
                }}
                placeholder="problème de départ ..."
                value={formData.analyse_probleme_depart}
              />

              {pourquois.map((pourquoi, index) => {
                const rowErrors = errors.pourquois?.[index] || {};
                return (
                  <Box
                    key={`pourquoi-${index + 1}`}
                    sx={{
                      alignItems: "flex-start",
                      backgroundColor: "#fff",
                      border: "1px solid #dce5ef",
                      borderRadius: "8px",
                      display: "grid",
                      gap: 1,
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "34px minmax(0, 1fr) 150px 34px",
                      },
                      mb: 1,
                      p: 1,
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: "center",
                        backgroundColor: "#edf2f7",
                        borderRadius: "50%",
                        color: "#36546f",
                        display: "flex",
                        fontSize: 12,
                        fontWeight: 800,
                        height: 28,
                        justifyContent: "center",
                        mt: { xs: 0, md: 0.3 },
                        width: 28,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box>
                      <Box
                        aria-invalid={Boolean(rowErrors.texte)}
                        aria-label={`Pourquoi ${index + 1}`}
                        component="textarea"
                        onChange={(event) =>
                          updateWhy(index, { texte: event.target.value })
                        }
                        placeholder={
                          index === 0
                            ? "Pourquoi ce problème existe-t-il ?"
                            : "Pourquoi la réponse précédente ?"
                        }
                        ref={index === 0 ? firstWhyRef : undefined}
                        rows={2}
                        sx={textareaSx(Boolean(rowErrors.texte))}
                        value={pourquoi.texte}
                      />
                      <Typography
                        color={rowErrors.texte ? "#d32f2f" : "transparent"}
                        fontSize={11}
                        sx={{ minHeight: 18, mt: 0.3 }}
                      >
                        {rowErrors.texte || "Aucune erreur"}
                      </Typography>
                    </Box>
                    <Autocomplete
                      disableCloseOnSelect
                      multiple
                      getOptionLabel={(option) => option?.label || "5M"}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      onChange={(_, options) =>
                        updateWhy(index, {
                          famille_5m: options
                            .map((option) => option.value)
                            .join(","),
                        })
                      }
                      options={FAMILLES_5M}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(rowErrors.famille_5m)}
                          helperText={rowErrors.famille_5m || " "}
                          placeholder="5M"
                          size="small"
                          sx={fieldSx}
                        />
                      )}
                      renderOption={(props, option, { selected }) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            alignItems: "center",
                            display: "flex",
                            gap: 1.2,
                            px: "12px !important",
                            py: "8px !important",
                          }}
                        >
                          <Checkbox checked={selected} size="small" sx={{ p: 0 }} />
                          <Typography component="span" fontSize={15}>
                            {option.icon}
                          </Typography>
                          <Typography
                            component="span"
                            flex={1}
                            fontSize={13}
                            fontWeight={800}
                            color="#3b4658"
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      )}
                      size="small"
                      value={selectedFamilies(pourquoi.famille_5m)}
                    />
                    <IconButton
                      aria-label={`Supprimer le pourquoi ${index + 1}`}
                      disabled={saving || pourquois.length <= 1}
                      onClick={() => onWhyRemove(index)}
                      size="small"
                      sx={{ color: "#536b91", mt: { xs: 0, md: 0.3 } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}

              <Button
                disabled={saving}
                fullWidth
                onClick={onWhyAdd}
                startIcon={<AddIcon />}
                sx={{
                  borderColor: "#c7d3e4",
                  color: "#36546f",
                  justifyContent: "flex-start",
                  mb: 1.5,
                  textTransform: "none",
                }}
                variant="outlined"
              >
                Ajouter un Pourquoi
              </Button>

              <FormLabel
                id="analyse-cause-racine-label"
                required
                sx={fieldLabelSx}
              >
                Cause racine confirmée
              </FormLabel>
              <Box
                aria-invalid={Boolean(errors.analyse_cause_racine)}
                aria-labelledby="analyse-cause-racine-label"
                component="textarea"
                onChange={(event) => updateRootCause(event.target.value)}
                placeholder="Cause racine organisationnelle, processus ou moyen..."
                ref={rootCauseRef}
                rows={3}
                sx={textareaSx(Boolean(errors.analyse_cause_racine))}
                value={formData.analyse_cause_racine}
              />
              <Typography
                color={errors.analyse_cause_racine ? "#d32f2f" : "transparent"}
                fontSize={11}
                sx={{ minHeight: 18, mt: 0.3 }}
              >
                {errors.analyse_cause_racine || "Aucune erreur"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 1,
                }}
              >
                <Button
                  disabled={saving}
                  onClick={handleLocalSave}
                  startIcon={
                    saving ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{
                    backgroundColor: "#0a96a5",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#087f8b" },
                  }}
                  variant="contained"
                >
                  Sauvegarder N Pourquoi
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "flex-end",
            mt: 2.5,
          }}
        >
          <Button
            disabled={saving}
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: "none" }}
            variant="outlined"
          >
            Retour
          </Button>
          <Button
            disabled={saving}
            onClick={() => handleSave("registre")}
            startIcon={
              saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />
            }
            sx={{
              backgroundColor: "#00a6b6",
              textTransform: "none",
              "&:hover": { backgroundColor: "#008f9d" },
            }}
            variant="contained"
          >
            Enregistrer la NC
          </Button>
          <Button
            disabled={saving}
            endIcon={saving ? null : <ArrowForwardIcon />}
            onClick={() => handleSave("action")}
            sx={{
              backgroundColor: "#00a6b6",
              textTransform: "none",
              "&:hover": { backgroundColor: "#008f9d" },
            }}
            variant="contained"
          >
            Étape suivante
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

AnalyseStep.propTypes = {
  formData: PropTypes.shape({
    analyse_cause_racine: PropTypes.string,
    analyse_probleme_depart: PropTypes.string,
    analyse_pourquois: PropTypes.arrayOf(
      PropTypes.shape({
        famille_5m: PropTypes.string,
        texte: PropTypes.string,
      })
    ),
    description_constat: PropTypes.string,
  }).isRequired,
  navigationEnabled: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onStepChange: PropTypes.func,
  onWhyAdd: PropTypes.func.isRequired,
  onWhyChange: PropTypes.func.isRequired,
  onWhyRemove: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
};

AnalyseStep.defaultProps = {
  navigationEnabled: false,
  onStepChange: () => {},
};

export default AnalyseStep;
